/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { AppItem, AppLanguage, PortfolioData } from './types';
import { createPortfolioText, loadPortfolioData } from './utils/portfolioData';
import AppDetailsModal from './components/AppDetailsModal';
import AppCarousel from './components/AppCarousel';
import { 
  Braces, Linkedin, Github, Mail, MessageCircle, Code2, Layers3, Sun, Moon, X
} from 'lucide-react';

const languages: AppLanguage[] = [
  { code: 'he', isRtl: true },
  { code: 'en', isRtl: false },
];

const languageOptions = [
  { code: 'he' as const, label: 'עברית', flag: '🇮🇱' },
  { code: 'en' as const, label: 'English', flag: '🇺🇸' },
];

const ALL_TYPES = '__all__';

const getWhatsappUrl = (value: string) => {
  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const phone = value.replace(/[^\d]/g, '');
  return `https://wa.me/${phone || encodeURIComponent(value)}`;
};

const getEmailParts = (email: string) => {
  const atIndex = email.lastIndexOf('@');

  if (atIndex <= 0) {
    return { local: email, domain: '' };
  }

  return {
    local: email.slice(0, atIndex),
    domain: email.slice(atIndex),
  };
};

const formatText = (template: string, values: Record<string, string | number>) => (
  Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
    template,
  )
);

const preloadImages = (sources: string[]) => Promise.all(
  Array.from(new Set(sources.filter(Boolean))).map(src => new Promise<void>(resolve => {
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => resolve();
    image.src = src;
  })),
);

export default function App() {
  const [lang, setLang] = useState<AppLanguage>(languages[0]);
  const [openMenu, setOpenMenu] = useState<'language' | 'theme' | null>(null);
  const [portfolio, setPortfolio] = useState<PortfolioData>({
    intro: '',
    text: createPortfolioText(),
    appVersion: '',
    appYear: '',
    logoSize: 64,
    stack: [],
    apps: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter State
  const [selectedType, setSelectedType] = useState(ALL_TYPES);

  // Active modal
  const [viewingApp, setViewingApp] = useState<AppItem | null>(null);

  // Dark Mode State
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') return true;
    if (saved === 'light') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Dark Mode Effect
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  // Fetch folder-based portfolio data
  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await loadPortfolioData(lang.code);

        await preloadImages(data.apps.flatMap(app => app.icon ? [app.icon] : []));
        
        setPortfolio(data);
      } catch (err: any) {
        console.error('Error fetching dynamic data:', err);
        setError(err.message || '');
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, [lang.code]);

  // Extract unique app types/categories
  const appTypes = Array.from(new Set(portfolio.apps.flatMap(app => app.types)));

  // Filter Logic
  const filteredApps = portfolio.apps.filter(app => {
    const matchesType = selectedType === ALL_TYPES || app.types.includes(selectedType);

    return matchesType;
  });
  const selectedTypeLabel = selectedType;
  const emailParts = portfolio.email ? getEmailParts(portfolio.email) : null;

  if (error && !loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 font-sans flex items-center justify-center p-6 transition-colors duration-300">
        <div
          dir="ltr"
          className="w-full max-w-md bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 rounded-2xl p-6 text-center"
        >
          <span className="text-lg">⚠️</span>
          <h1 className="font-bold text-rose-800 dark:text-rose-200 mt-2 mb-1">
            Unable to load this page
          </h1>
          <p className="text-sm text-rose-600 dark:text-rose-400 mb-4">
            Check your network connection and try again.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-1.5 text-xs font-semibold bg-rose-600 text-white rounded-lg hover:bg-rose-500 transition-colors cursor-pointer"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      dir={lang.isRtl ? 'rtl' : 'ltr'}
      className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 font-sans selection:bg-indigo-500 selection:text-white pb-16 transition-colors duration-300"
    >
      {/* Upper Navigation Header */}
      <nav className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-40 shadow-xs py-3.5 transition-colors duration-300">
        <div className="w-full flex flex-wrap items-center justify-between gap-x-4 gap-y-3 ps-[10px] pe-4 sm:pe-6">
          
          {/* Logo Brand */}
          <div className="flex basis-full sm:basis-auto sm:flex-1 items-center gap-[40px] min-w-0">
            <img
              src="/data/main/icons/title-ico.png"
              alt=""
              className="object-contain shrink-0"
              style={{ width: portfolio.logoSize, height: portfolio.logoSize }}
            />
            <div className={`min-w-0 ${lang.isRtl ? 'text-right' : 'text-left'}`}>
              <h1 className="font-bold text-slate-900 dark:text-white tracking-tight text-sm sm:text-base font-display truncate">
                {portfolio.text.nav_title}
              </h1>
              <div className={`flex items-baseline gap-x-2 text-[10px] font-bold overflow-hidden whitespace-nowrap ${
                lang.isRtl ? 'font-sans' : 'font-mono uppercase tracking-wider'
              }`}>
                <span className="text-teal-700 dark:text-teal-300 shrink-0">
                  {portfolio.text.nav_subtitle}
                </span>
                {portfolio.stack.length > 0 && (
                  <span className="text-[9px] text-slate-950 dark:text-white truncate">
                    {portfolio.stack.join(' | ')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Nav Controls */}
          <div className="flex w-full sm:w-auto items-center gap-2 justify-start sm:justify-end shrink-0">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setOpenMenu(current => current === 'language' ? null : 'language')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-200 transition-all cursor-pointer"
              >
                <span className="text-sm">{portfolio.text.language_flag}</span>
                <span>{portfolio.text.language_label}</span>
              </button>
              {openMenu === 'language' && (
                <div className={`absolute top-full mt-2 min-w-32 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg p-1.5 z-50 ${
                  lang.isRtl ? 'right-0' : 'left-0'
                }`}>
                  {languageOptions.map(option => (
                    <button
                      key={option.code}
                      type="button"
                      onClick={() => {
                        const selected = languages.find(language => language.code === option.code);
                        if (selected) setLang(selected);
                        setOpenMenu(null);
                      }}
                      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                        lang.code === option.code
                          ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-300'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                      }`}
                    >
                      <span>{option.flag}</span>
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Dark Mode Toggle */}
            <div className="relative">
              <button
                onClick={() => setOpenMenu(current => current === 'theme' ? null : 'theme')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-200 transition-all cursor-pointer"
                aria-label="Toggle theme"
                title={portfolio.text.theme_toggle_title}
              >
                {darkMode ? (
                  <>
                    <Moon size={14} className="text-indigo-500 dark:text-indigo-400 shrink-0" />
                    <span className="hidden sm:inline">{portfolio.text.theme_dark}</span>
                  </>
                ) : (
                  <>
                    <Sun size={14} className="text-amber-500 shrink-0" />
                    <span className="hidden sm:inline">{portfolio.text.theme_light}</span>
                  </>
                )}
              </button>
              {openMenu === 'theme' && (
                <div className={`absolute top-full mt-2 min-w-28 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg p-1.5 z-50 ${
                  lang.isRtl ? 'right-0' : 'left-0'
                }`}>
                  <button
                    type="button"
                    onClick={() => {
                      setDarkMode(false);
                      setOpenMenu(null);
                    }}
                    className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                      !darkMode
                        ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-300'
                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Sun size={14} className="text-amber-500 shrink-0" />
                    <span>{portfolio.text.theme_light}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDarkMode(true);
                      setOpenMenu(null);
                    }}
                    className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                      darkMode
                        ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-300'
                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Moon size={14} className="text-indigo-500 dark:text-indigo-400 shrink-0" />
                    <span>{portfolio.text.theme_dark}</span>
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      </nav>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        
        {/* Profile Introduction Banner Card (RTL or LTR based on Language) */}
        <section 
          className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 flex flex-col md:flex-row justify-between items-stretch gap-6 shadow-xs relative overflow-hidden transition-colors duration-300 ${
            lang.isRtl ? 'text-right' : 'text-left'
          }`}
        >
          {/* Subtle background radial aura */}
          <div className="absolute -left-12 -bottom-12 w-48 h-48 rounded-full bg-indigo-500/5 dark:bg-indigo-500/10 blur-3xl animate-pulse" />
          
          <div className="space-y-4 relative z-10 max-w-3xl flex-1">
            <div className="flex items-center gap-2 justify-start">
              <Braces size={20} className="text-indigo-500 dark:text-indigo-400 shrink-0" />
              <h2 className="text-lg sm:text-xl font-bold font-display text-slate-900 dark:text-white tracking-tight">
                {portfolio.text.profile_title}
              </h2>
            </div>
            
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed font-normal">
              {portfolio.intro}
            </p>

            {/* Profile Contact Details */}
            <div className="flex flex-wrap gap-2.5 pt-2 justify-start">
              {portfolio.linkedin && (
                <a 
                href={portfolio.linkedin}
                target="_blank" 
                rel="noreferrer" 
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-500 py-1.5 px-3 rounded-xl transition-all"
              >
                <Linkedin size={13} />
                <span>{portfolio.text.contact_linkedin}</span>
              </a>
              )}
              {portfolio.github && (
                <a 
                href={portfolio.github}
                target="_blank" 
                rel="noreferrer" 
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-500 py-1.5 px-3 rounded-xl transition-all"
              >
                <Github size={13} />
                <span>{portfolio.text.contact_github}</span>
              </a>
              )}
              {portfolio.email && (
                <a 
                href={`mailto:${portfolio.email}`}
                className="inline-flex max-w-full min-w-0 items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-500 py-1.5 px-3 rounded-xl transition-all"
              >
                <Mail size={13} className="shrink-0" />
                <span dir="ltr" className="flex min-w-0 overflow-hidden">
                  <span className="min-w-0 truncate">{emailParts?.local}</span>
                  <span className="shrink-0">{emailParts?.domain}</span>
                </span>
              </a>
              )}
              {portfolio.whatsapp && (
                <a
                  href={getWhatsappUrl(portfolio.whatsapp)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-500 py-1.5 px-3 rounded-xl transition-all"
                >
                  <MessageCircle size={13} />
                  <span>{portfolio.text.contact_whatsapp}</span>
                </a>
              )}
            </div>
          </div>

          {/* Sidebar Stats Area */}
          <div className={`flex sm:flex-row md:flex-col gap-3 justify-center shrink-0 w-full md:w-56 relative z-10 border-t md:border-t-0 border-slate-100 dark:border-slate-800 pt-4 md:pt-0 ${
            lang.isRtl ? 'md:border-r md:pr-6 md:mr-2' : 'md:border-l md:pl-6 md:ml-2'
          }`}>
            <div className="flex-1 md:flex-none text-center bg-slate-50/60 dark:bg-slate-800/60 px-4 py-3 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 transition-colors duration-300">
              <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider block">
                {portfolio.text.experience_label}
              </span>
              <span className="text-base font-extrabold text-indigo-600 dark:text-indigo-400 font-display mt-0.5 block">{portfolio.text.experience_value}</span>
            </div>
          </div>
        </section>

        {/* Search, Filter and Sort Toolbar */}
        <section className="space-y-4">
          <div className="flex items-center p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs transition-colors duration-300 justify-start min-w-0">
            {/* Filter Tabs by framework types */}
            <div className="flex items-center gap-2 min-w-0 w-full justify-start">
              <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider shrink-0">
                {portfolio.text.filter_type_label}
              </span>
              <div dir="ltr" className="min-w-0 overflow-x-auto bg-slate-100 dark:bg-slate-850 p-0.5 rounded-xl border border-slate-200 dark:border-slate-750 transition-colors duration-300">
                <div className="inline-flex min-w-max items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setSelectedType(ALL_TYPES)}
                    disabled={selectedType === ALL_TYPES}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors shrink-0 ${
                      selectedType === ALL_TYPES
                        ? 'opacity-20 text-slate-400 dark:text-slate-600 cursor-default'
                        : 'text-slate-400 hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-200 hover:bg-white dark:hover:bg-slate-750 cursor-pointer'
                    }`}
                  >
                    <X size={13} />
                  </button>
                  {appTypes.map(type => (
                    <button
                      key={type}
                      onClick={() => setSelectedType(type)}
                      dir="ltr"
                      className={`h-7 px-3 flex items-center text-[11px] font-bold rounded-lg transition-colors cursor-pointer shrink-0 ${
                        selectedType === type 
                          ? 'bg-white dark:bg-slate-750 text-indigo-600 dark:text-indigo-400 shadow-xs' 
                          : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Loading/Error/List Section */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <span className="sr-only">{portfolio.text.loading_intro}</span>
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800 p-5 rounded-2xl h-44 animate-pulse flex flex-col justify-between transition-colors duration-300">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-200 dark:bg-slate-800 rounded-xl" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-4 bg-slate-200 dark:bg-slate-800 rounded-sm w-3/4" />
                    <div className="h-3 bg-slate-150 dark:bg-slate-800 rounded-sm w-1/2" />
                  </div>
                </div>
                <div className="h-10 bg-slate-100 dark:bg-slate-850 rounded-xl" />
              </div>
            ))}
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-12 text-center max-w-lg mx-auto transition-colors duration-300">
            <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Layers3 size={22} />
            </div>
            <h4 className="font-bold text-slate-800 dark:text-slate-100 text-base mb-1">
              {portfolio.text.empty_state_title}
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 max-w-sm mx-auto">
              {formatText(portfolio.text.empty_state_description, { type: selectedTypeLabel })}
            </p>
            <div className="flex gap-2 justify-center">
              <button 
                onClick={() => { setSelectedType(ALL_TYPES); }}
                className="px-4 py-2 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-semibold transition-colors cursor-pointer"
              >
                {portfolio.text.empty_state_action}
              </button>
            </div>
          </div>
        ) : (
          <AppCarousel
            apps={filteredApps}
            language={lang}
            text={portfolio.text}
            autoPlayPaused={viewingApp !== null}
            onSelect={(selected) => setViewingApp(selected)}
          />
        )}
      </main>

      {/* Footer credentials and specification */}
      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-10 mt-16 text-xs text-slate-400 dark:text-slate-500 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg text-indigo-600 dark:text-indigo-400 transition-colors duration-300">
              <Code2 size={14} />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-semibold text-slate-500 dark:text-slate-400">{portfolio.text.footer_title}</span>
              {portfolio.appVersion && (
                <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-600">
                  {formatText(portfolio.text.footer_version, { version: portfolio.appVersion })}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {portfolio.appYear && (
              <span className="font-mono text-[10px]">{portfolio.appYear}</span>
            )}
          </div>
        </div>
      </footer>

      {/* App Details Inspection Modal Overlay */}
      {viewingApp && (
        <AppDetailsModal
          app={viewingApp}
          language={lang}
          text={portfolio.text}
          onClose={() => setViewingApp(null)}
        />
      )}
    </div>
  );
}
